// ============================================================================
// Small, dependency-free formatting helpers for the business report.
// Locale-aware via Intl; currency rendering routes through the shared
// lib/currency helper so the trainer's HK$ / US$ / PS symbol convention is
// identical to the client editor and the session log.
// ============================================================================
import { currencySymbol, formatMoney } from "../../lib/currency";
import { intlLocale } from "../../i18n/config";

/** Round + format a number with a fixed number of decimals (locale grouping). */
export function fmtNum(
  value: number,
  decimals = 0,
  locale?: string,
): string {
  return value.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format a money amount in the trainer's currency using the shared symbol
 * convention (HK$ / US$ / PS), e.g. "HK$ 1,250" / "US$ 62.50". An explicit
 * `symbol` (the API's pre-resolved `currency_symbol`) wins; otherwise it's
 * derived from the bare code. No currency set -> a clean grouped number.
 */
export function fmtMoney(
  value: number,
  currency?: string | null,
  locale?: string,
  symbol?: string | null,
): string {
  return formatMoney(value, currency, { symbol, locale });
}

/**
 * The currency symbol/prefix used by the animated count-up tiles (which render
 * the digits themselves). Returns e.g. "HK$", "US$", "PS" or "" when no
 * currency is set. An explicit pre-resolved `symbol` wins over the code map.
 */
export function currencyPrefix(
  currency?: string | null,
  _locale?: string,
  symbol?: string | null,
): string {
  const explicit = (symbol ?? "").trim();
  if (explicit) return explicit;
  return currencySymbol(currency);
}

/** Format a number of hours, e.g. "12.5 h" (1 dp, trims a trailing ".0"). */
export function fmtHours(value: number, locale?: string): string {
  const decimals = Number.isInteger(value) ? 0 : 1;
  return fmtNum(value, decimals, locale);
}

/**
 * Format a "YYYY-MM" month bucket to a short, locale-aware axis label, e.g.
 * "Jan", "Mar '25". Falls back to the raw bucket if it can't be parsed.
 */
export function fmtMonth(bucket: string, locale?: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(bucket);
  if (!m) return bucket;
  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  const d = new Date(Date.UTC(year, month, 1));
  if (Number.isNaN(d.getTime())) return bucket;
  return d.toLocaleDateString(intlLocale(locale), {
    month: "short",
    timeZone: "UTC",
  });
}

/** A friendly, locale-aware "today" label for the masthead. */
export function fmtDate(iso: string | null, locale?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(intlLocale(locale), {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Clamp a number to a [min, max] range. */
export function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}
